import {
    BehaviorSubject,
    EMPTY,
    ReplaySubject,
    combineLatest,
    fromEvent,
    merge,
} from 'rxjs';
import { share, switchMap, tap } from 'rxjs/operators';
import { fetchJson } from './http.js';
import { renderTemplate } from './ui.js';

/**
 * Shared Observable that fetches the full topic list once and caches the
 * result for all widget instances. Emits an array of { id, name } objects.
 */
let topics$ = fetchJson('/api/topics').pipe(
    share({
        connector: () => new ReplaySubject(1),
        resetOnError: true,
        resetOnRefCountZero: true,
        resetOnComplete: true,
    })
);

/**
 * Creates a topics-selection widget inside the given container element.
 * Clones the `topics-widget` template, mounts it in containerEl, then wires
 * all reactive logic: loading the topic list, rendering selected-topic pills
 * with remove buttons, and reacting to the dropdown selection.
 *
 * Accepts an optional initialTopicIds array of integer topic IDs so the
 * edit-view can pre-populate the selection from an existing card's topic_ids.
 *
 * Returns an object with:
 *   widget$          – Observable to include in the parent view's merge(...)
 *   getSelectedTopics() – returns the current array of selected topic objects
 *   reset()          – clears the selection (used by write-view's clear button)
 */
export function createTopicsWidget(containerEl, initialTopicIds) {
    let widgetEl = renderTemplate('topics-widget');
    containerEl.appendChild(widgetEl);

    let selectEl = widgetEl.querySelector('.topics-widget-select');
    let selectedContainer = widgetEl.querySelector('.topics-widget-selected');

    let selectedTopics$ = new BehaviorSubject([]);

    let populateSelect$ = topics$.pipe(
        tap((topics) => {
            selectEl.innerHTML = '<option value="">Thema auswählen...</option>';
            for (let topic of topics) {
                let option = document.createElement('option');
                option.value = topic.id;
                option.textContent = topic.name;
                selectEl.appendChild(option);
            }
            if (initialTopicIds && initialTopicIds.length) {
                let resolved = initialTopicIds
                    .map((id) => topics.find((t) => t.id === id))
                    .filter(Boolean);
                selectedTopics$.next(resolved);
            }
        })
    );

    let renderSelected$ = selectedTopics$.pipe(
        switchMap((selectedTopics) => {
            selectedContainer.innerHTML = '';
            let obs = [];
            for (let topic of selectedTopics) {
                let pill = document.createElement('span');
                pill.className = 'topic-pill';
                pill.setAttribute('data-topic-id', topic.id);
                pill.textContent = topic.name;
                let removeBtn = document.createElement('button');
                removeBtn.textContent = '×';
                removeBtn.className = 'remove-topic';
                obs.push(
                    fromEvent(removeBtn, 'click').pipe(
                        tap(() => {
                            selectedTopics$.next(
                                selectedTopics$.value.filter(
                                    (t) => t.id !== topic.id
                                )
                            );
                        })
                    )
                );
                pill.appendChild(removeBtn);
                selectedContainer.appendChild(pill);
            }
            return obs.length ? merge(...obs) : EMPTY;
        })
    );

    let topicsSelect$ = combineLatest([
        topics$,
        fromEvent(selectEl, 'change'),
    ]).pipe(
        tap(([topics, e]) => {
            if (!e.target.value) {
                return;
            }
            let topicId = parseInt(e.target.value);
            let topic = topics.find((t) => t.id === topicId);
            if (topic && !selectedTopics$.value.find((t) => t.id === topicId)) {
                selectedTopics$.next([...selectedTopics$.value, topic]);
                selectEl.value = '';
            }
        })
    );

    let widget$ = merge(
        topics$,
        populateSelect$,
        renderSelected$,
        topicsSelect$
    );

    return {
        widget$,
        getSelectedTopics() {
            return selectedTopics$.value;
        },
        reset() {
            selectedTopics$.next([]);
        },
    };
}
